import { useState, useEffect, useRef, type FormEvent } from 'react'
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom'
import { AlertCircle, Mail, Shield, CheckCircle2, Loader2 } from 'lucide-react'
import { useAuth } from '@/contexts/AuthContext'
import { assetUrl } from '@/utils/assetUrl'

type Step = 'email' | 'check_email' | 'setup_totp' | 'verify'

interface TotpSetupData {
  qr_uri: string
  secret: string
  email: string
}

export default function LoginPage() {
  const navigate = useNavigate()
  const location = useLocation()
  const [searchParams] = useSearchParams()
  const { setUserFromResponse } = useAuth()

  const [email, setEmail] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [step, setStep] = useState<Step>('email')
  const [totpSetup, setTotpSetup] = useState<TotpSetupData | null>(null)
  const [code, setCode] = useState('')
  const codeRef = useRef<HTMLInputElement>(null)

  const from = (location.state as { from?: { pathname: string } })?.from?.pathname || '/'

  // Magic-Link Token aus URL prüfen
  useEffect(() => {
    const magicToken = searchParams.get('magic')
    if (magicToken) {
      handleMagicLink(magicToken)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleMagicLink = async (token: string) => {
    setIsSubmitting(true)
    setError(null)
    try {
      const res = await fetch(`/api/auth/magic/${encodeURIComponent(token)}`)
      if (!res.ok) {
        const data = await res.json().catch(() => ({ detail: 'Link ungültig oder abgelaufen' }))
        throw new Error(data.detail)
      }
      const data = await res.json()
      setTotpSetup(data)
      setEmail(data.email)
      setStep('setup_totp')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Link ungültig')
      setStep('email')
    } finally {
      setIsSubmitting(false)
    }
  }

  // Step 1: E-Mail absenden
  const handleEmailSubmit = async (e: FormEvent) => {
    e.preventDefault()
    if (!email.trim()) {
      setError('Bitte E-Mail-Adresse eingeben')
      return
    }

    setError(null)
    setIsSubmitting(true)
    try {
      const res = await fetch('/api/auth/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim() }),
      })
      const data = await res.json()
      if (!res.ok) throw new Error(data.detail || 'Fehler')

      if (data.step === 'verify') {
        setStep('verify')
      } else if (data.step === 'check_email') {
        setStep('check_email')
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Login fehlgeschlagen')
    } finally {
      setIsSubmitting(false)
    }
  }

  // Step: TOTP-Code verifizieren (bestehende User)
  const handleVerify = async (e: FormEvent) => {
    e.preventDefault()
    if (!code.trim() || code.length !== 6) {
      setError('Bitte 6-stelligen Code eingeben')
      return
    }

    setError(null)
    setIsSubmitting(true)
    try {
      const res = await fetch('/api/auth/totp/verify', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim(), code: code.trim() }),
      })
      const data = await res.json()
      if (!res.ok) throw new Error(data.detail || 'Code ungültig')

      setUserFromResponse(data.user, data.firmen)
      const firmen = data.firmen as { D365_ContactID: string; Firmenname: string; D365_AccountID: string; token: string }[] | undefined
      if (firmen && firmen.length > 1 && data.user?.Role !== 'superadmin' && from === '/') {
        navigate('/select-firma', { replace: true, state: { firmen } })
      } else if (firmen && firmen.length === 1 && data.user?.Role !== 'superadmin' && from === '/') {
        navigate(`/firma/${firmen[0].token}`, { replace: true })
      } else {
        navigate(from, { replace: true })
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Verifizierung fehlgeschlagen')
      setCode('')
      codeRef.current?.focus()
    } finally {
      setIsSubmitting(false)
    }
  }

  // Step: TOTP-Setup abschließen
  const handleSetupConfirm = async (e: FormEvent) => {
    e.preventDefault()
    if (!totpSetup || !code.trim() || code.length !== 6) {
      setError('Bitte 6-stelligen Code eingeben')
      return
    }

    setError(null)
    setIsSubmitting(true)
    try {
      const res = await fetch('/api/auth/totp/setup', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: totpSetup.email, code: code.trim() }),
      })
      const data = await res.json()
      if (!res.ok) throw new Error(data.detail || 'Code ungültig')

      setUserFromResponse(data.user, data.firmen)
      setSuccess('Authenticator erfolgreich eingerichtet!')
      setTimeout(() => {
        const firmen = data.firmen as { D365_ContactID: string; Firmenname: string; D365_AccountID: string; token: string }[] | undefined
        if (firmen && firmen.length > 1 && data.user?.Role !== 'superadmin' && from === '/') {
          navigate('/select-firma', { replace: true, state: { firmen } })
        } else if (firmen && firmen.length === 1 && data.user?.Role !== 'superadmin' && from === '/') {
          navigate(`/firma/${firmen[0].token}`, { replace: true })
        } else {
          navigate(from, { replace: true })
        }
      }, 1500)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Setup fehlgeschlagen')
      setCode('')
      codeRef.current?.focus()
    } finally {
      setIsSubmitting(false)
    }
  }

  // Focus code input when switching to code step
  useEffect(() => {
    if ((step === 'verify' || step === 'setup_totp') && codeRef.current) {
      setTimeout(() => codeRef.current?.focus(), 100)
    }
  }, [step])

  return (
    <div className="min-h-screen flex items-center justify-center bg-fom-500 p-6">
      <div className="w-full max-w-[1100px] bg-white rounded-2xl shadow-modal overflow-hidden flex min-h-[600px]">

        {/* ── Linke Seite: Hero-Bild ── */}
        <div className="hidden md:flex md:w-1/2 relative">
          <img
            src={assetUrl('/login-bg.jpg')}
            alt="Modernes Büro"
            className="absolute inset-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-black/20" />

          <div className="absolute bottom-8 left-8 right-8">
            <h2 className="text-white text-[1.7rem] font-display font-black leading-tight mb-3">
              Das Portal für Unternehmen
            </h2>
            <p className="text-white/85 text-[0.9rem] leading-relaxed">
              Personalverantwortliche erhalten über MyFOM Einblicke in den jeweiligen
              Stand des Studiums ihrer Mitarbeiter
            </p>
          </div>
        </div>

        {/* ── Rechte Seite: Login-Flow ── */}
        <div className="w-full md:w-1/2 flex flex-col justify-center px-10 py-12 lg:px-14 bg-[#FAFAFA]">
          {/* Mobile Logo */}
          <div className="md:hidden flex items-center gap-1.5 mb-8 justify-center">
            <span className="text-gray-900 text-2xl font-display italic font-bold tracking-tight">My</span>
            <div className="bg-fom-500 rounded-md px-2.5 py-1">
              <span className="text-white text-2xl font-display font-black tracking-tight">FOM</span>
            </div>
          </div>

          {error && (
            <div className="flex items-start gap-2 bg-red-50 border border-red-200 text-red-700 rounded-xl px-4 py-3 mb-6 text-sm">
              <AlertCircle size={16} className="mt-0.5 shrink-0" />
              <span>{error}</span>
            </div>
          )}

          {success && (
            <div className="flex items-start gap-2 bg-emerald-50 border border-emerald-200 text-emerald-700 rounded-xl px-4 py-3 mb-6 text-sm">
              <CheckCircle2 size={16} className="mt-0.5 shrink-0" />
              <span>{success}</span>
            </div>
          )}

          {/* ───── STEP: E-Mail eingeben ───── */}
          {step === 'email' && (
            <form onSubmit={handleEmailSubmit} className="space-y-6">
              <div>
                <label htmlFor="email" className="block text-sm font-semibold text-gray-800 mb-2">
                  E-Mail-Adresse
                </label>
                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  autoComplete="email"
                  autoFocus
                  className="w-full px-5 py-3.5 border border-gray-300 rounded-full text-sm
                             bg-white focus:ring-2 focus:ring-fom-500 focus:border-fom-500 outline-none
                             transition-all placeholder:text-gray-400"
                  placeholder="max.mustermann@firma.de"
                />
              </div>

              <button
                type="submit"
                disabled={isSubmitting || !email.trim()}
                className="w-full py-3.5 bg-fom-500 text-white text-base font-bold rounded-full
                           hover:bg-fom-600 active:bg-fom-700
                           disabled:opacity-50 disabled:cursor-not-allowed
                           transition-all duration-200 shadow-sm hover:shadow-md"
              >
                {isSubmitting ? (
                  <span className="flex items-center justify-center gap-2">
                    <Loader2 size={20} className="animate-spin" />
                    Prüfe...
                  </span>
                ) : (
                  'Weiter'
                )}
              </button>
            </form>
          )}

          {/* ───── STEP: E-Mail prüfen ───── */}
          {step === 'check_email' && (
            <div className="text-center space-y-6">
              <div className="w-16 h-16 rounded-full bg-fom-50 text-fom-500 flex items-center justify-center mx-auto">
                <Mail size={28} />
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">E-Mail prüfen</h3>
                <p className="text-sm text-gray-600 leading-relaxed">
                  Wir haben einen Anmelde-Link an<br />
                  <strong className="text-gray-900">{email}</strong><br />
                  gesendet. Klicken Sie auf den Link, um fortzufahren.
                </p>
              </div>
              <p className="text-xs text-gray-400">
                Der Link ist 15 Minuten gültig.
              </p>
              <button
                type="button"
                onClick={() => { setStep('email'); setError(null) }}
                className="text-sm text-fom-500 hover:text-fom-700 underline transition-colors"
              >
                Andere E-Mail verwenden
              </button>
            </div>
          )}

          {/* ───── STEP: TOTP-Setup (QR-Code) ───── */}
          {step === 'setup_totp' && totpSetup && (
            <form onSubmit={handleSetupConfirm} className="space-y-5">
              <div className="text-center">
                <div className="w-14 h-14 rounded-full bg-fom-50 text-fom-500 flex items-center justify-center mx-auto mb-3">
                  <Shield size={24} />
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-1">Authenticator einrichten</h3>
                <p className="text-[0.8rem] text-gray-500">
                  Scannen Sie den QR-Code mit Ihrer Authenticator-App<br />
                  (z.B. Google Authenticator, Microsoft Authenticator)
                </p>
              </div>

              <div className="flex justify-center">
                <img
                  src={totpSetup.qr_uri}
                  alt="QR-Code für Authenticator"
                  className="w-48 h-48 rounded-xl border border-gray-200 bg-white p-2"
                />
              </div>

              <details className="text-center">
                <summary className="text-xs text-gray-400 cursor-pointer hover:text-gray-600">
                  Manueller Code
                </summary>
                <code className="block mt-2 text-xs bg-gray-100 rounded-lg px-4 py-2 text-gray-700 font-mono tracking-widest break-all select-all">
                  {totpSetup.secret}
                </code>
              </details>

              <div>
                <label htmlFor="setup-code" className="block text-sm font-semibold text-gray-800 mb-2 text-center">
                  6-stelligen Code eingeben
                </label>
                <input
                  ref={codeRef}
                  id="setup-code"
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  maxLength={6}
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  required
                  autoComplete="one-time-code"
                  className="w-full px-5 py-4 border border-gray-300 rounded-full text-center text-2xl font-mono tracking-[0.5em]
                             bg-white focus:ring-2 focus:ring-fom-500 focus:border-fom-500 outline-none transition-all"
                  placeholder="000000"
                />
              </div>

              <button
                type="submit"
                disabled={isSubmitting || code.length !== 6}
                className="w-full py-3.5 bg-fom-500 text-white text-base font-bold rounded-full
                           hover:bg-fom-600 active:bg-fom-700
                           disabled:opacity-50 disabled:cursor-not-allowed
                           transition-all duration-200 shadow-sm hover:shadow-md"
              >
                {isSubmitting ? (
                  <span className="flex items-center justify-center gap-2">
                    <Loader2 size={20} className="animate-spin" />
                    Prüfe...
                  </span>
                ) : (
                  'Bestätigen & Anmelden'
                )}
              </button>
            </form>
          )}

          {/* ───── STEP: TOTP-Code verifizieren (bestehende User) ───── */}
          {step === 'verify' && (
            <form onSubmit={handleVerify} className="space-y-6">
              <div className="text-center">
                <div className="w-14 h-14 rounded-full bg-fom-50 text-fom-500 flex items-center justify-center mx-auto mb-3">
                  <Shield size={24} />
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-1">Authenticator-Code</h3>
                <p className="text-[0.8rem] text-gray-500">
                  Geben Sie den 6-stelligen Code aus Ihrer<br />Authenticator-App ein
                </p>
              </div>

              <div>
                <input
                  ref={codeRef}
                  id="verify-code"
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  maxLength={6}
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  required
                  autoComplete="one-time-code"
                  className="w-full px-5 py-4 border border-gray-300 rounded-full text-center text-2xl font-mono tracking-[0.5em]
                             bg-white focus:ring-2 focus:ring-fom-500 focus:border-fom-500 outline-none transition-all"
                  placeholder="000000"
                />
              </div>

              <button
                type="submit"
                disabled={isSubmitting || code.length !== 6}
                className="w-full py-3.5 bg-fom-500 text-white text-base font-bold rounded-full
                           hover:bg-fom-600 active:bg-fom-700
                           disabled:opacity-50 disabled:cursor-not-allowed
                           transition-all duration-200 shadow-sm hover:shadow-md"
              >
                {isSubmitting ? (
                  <span className="flex items-center justify-center gap-2">
                    <Loader2 size={20} className="animate-spin" />
                    Prüfe...
                  </span>
                ) : (
                  'Anmelden'
                )}
              </button>

              <button
                type="button"
                onClick={() => { setStep('email'); setCode(''); setError(null) }}
                className="w-full text-sm text-fom-500 hover:text-fom-700 underline transition-colors"
              >
                Zurück
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  )
}
